import { Product, Category } from '../types';
import { createUnifiedApiService, UnifiedApiService, UnifiedProduct, UnifiedSearchOptions } from './unifiedApiService';
import { authService } from './authService';

interface ProductService {
  getProducts: () => Promise<Product[]>;
  getAllProducts: () => Promise<Product[]>;
  getCategories: () => Promise<Category[]>;
  getProductById: (id: string) => Promise<Product | null>;
  searchProducts: (query: string, categoryId?: string) => Promise<Product[]>;
  getProductsByCategory: (categoryId: string) => Promise<Product[]>;
  clearCache: () => void;
}

class ProductServiceImpl implements ProductService {
  private productCache: Map<string, { products: Product[], timestamp: number }> = new Map();
  private categoryCache: { categories: Category[], timestamp: number } | null = null;
  private apiServiceCache: UnifiedApiService | null = null;
  private currentUser: string | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  private getApiService(): UnifiedApiService | null {
    const user = authService.getCurrentUser();
    if (!user) {
      console.warn('No user logged in - cannot access products');
      return null;
    }
    
    // Check if we can reuse the cached API service
    const userKey = `${user.username}-${user.system}`;
    if (this.apiServiceCache && this.currentUser === userKey) {
      console.log('🔄 ProductService: Reusing cached API service instance');
      return this.apiServiceCache;
    }
    
    // Create new API service and cache it
    console.log('🔍 ProductService: Creating new API service for user:', {
      username: user.username,
      system: user.system,
    });
    
    this.apiServiceCache = createUnifiedApiService(user);
    this.currentUser = userKey;
    
    return this.apiServiceCache;
  }

  private convertUnifiedProductToProduct(unifiedProduct: UnifiedProduct): Product {
    return {
      id: unifiedProduct.id,
      itemNumber: unifiedProduct.sku,
      description: unifiedProduct.name,
      category: unifiedProduct.category,
      manufacturer: '',
      model: '',
      specifications: {
        Unit: unifiedProduct.unitCode || ''
      },
      status: unifiedProduct.status, // Use the actual status from UnifiedProduct
      location: '',
      tags: []
    };
  }

  async getProducts(): Promise<Product[]> {
    console.warn('⚠️ ProductService.getProducts() - Consider using searchProducts() with specific criteria for better performance');
    const api = this.getApiService();
    if (!api) {
      return [];
    }

    try {
      console.log('📦 ProductService: Getting all products (WARNING: Downloads entire catalog)...');
      // Use searchProducts with no parameters to get ALL products - this downloads the entire catalog
      const unifiedProducts = await api.searchProducts({});
      const products = unifiedProducts.map(p => this.convertUnifiedProductToProduct(p));
      console.log(`✅ ProductService: Got ${products.length} products from ${api.getCurrentSystem()} (entire catalog)`);
      return products;
    } catch (error) {
      console.error('❌ ProductService: Failed to load products:', error);
      return [];
    }
  }

  async getAllProducts(): Promise<Product[]> {
    console.warn('⚠️ ProductService.getAllProducts() - Consider using searchProducts() with specific criteria for better performance');
    return this.getProducts();
  }

  async getCategories(): Promise<Category[]> {
    // Check cache first
    if (this.categoryCache && this.isCacheValid(this.categoryCache.timestamp)) {
      console.log('📦 ProductService: Using cached categories');
      return this.categoryCache.categories;
    }

    const api = this.getApiService();
    if (!api) {
      return [];
    }

    try {
      console.log('🏷️ ProductService: Loading categories from real API...');
      const categoryNames = await api.getProductCategories();
      const categories: Category[] = categoryNames.map((name, index) => ({
        id: `cat-${index + 1}`,
        name: name,
        description: `${name} category`,
        color: '#' + Math.floor(Math.random()*16777215).toString(16) // Random color for now
      }));
      
      // Cache the categories
      this.categoryCache = {
        categories,
        timestamp: Date.now()
      };
      
      console.log(`✅ ProductService: Loaded ${categories.length} categories from ${api.getCurrentSystem()} API (cached for 5 minutes)`);
      return categories;
    } catch (error) {
      console.error('❌ ProductService: Failed to load categories:', error);
      return [];
    }
  }

  async getProductById(id: string): Promise<Product | null> {
    const api = this.getApiService();
    if (!api) {
      return null;
    }

    try {
      const unifiedProduct = await api.getProductById(id);
      if (!unifiedProduct) {
        // Fallback: try searching for the product
        const products = await api.searchProducts({ limit: 100 });
        const found = products.find(p => p.id === id);
        if (found) {
          return this.convertUnifiedProductToProduct(found);
        }
        return null;
      }
      return this.convertUnifiedProductToProduct(unifiedProduct);
    } catch (error) {
      console.error('❌ ProductService: Failed to get product by ID:', error);
      return null;
    }
  }

  async searchProducts(query: string, categoryId?: string): Promise<Product[]> {
    const api = this.getApiService();
    if (!api) {
      return [];
    }

    // Create cache key based on search parameters
    const cacheKey = `${query || ''}|${categoryId || ''}`;
    
    // Check cache first
    const cached = this.productCache.get(cacheKey);
    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log(`📦 ProductService: Using cached results for "${cacheKey}" (${cached.products.length} products)`);
      return cached.products;
    }

    try {
      console.log('🔍 ProductService: Using server-side search and filtering...', { query, categoryId });
      
      // Build search options for server-side filtering
      const searchOptions: any = {};
      
      if (query && query.trim()) {
        searchOptions.query = query.trim();
      }
      
      if (categoryId) {
        const categories = await this.getCategories();
        const category = categories.find(c => c.id === categoryId);
        if (category) {
          searchOptions.category = category.name;
          console.log(`🏷️ ProductService: Using server-side category filter: "${category.name}"`);
        }
      }
      
      // Use server-side search with filtering
      const unifiedProducts = await api.searchProducts(searchOptions);
      
      if (unifiedProducts.length === 0) {
        console.log('⚠️ ProductService: No products found with server-side filtering');
        return [];
      }

      console.log(`📊 ProductService: Got ${unifiedProducts.length} server-filtered products`);

      // Convert to Product format (no client-side filtering needed!)
      const products = unifiedProducts.map(p => this.convertUnifiedProductToProduct(p));
      
      // Cache the filtered results
      this.productCache.set(cacheKey, {
        products,
        timestamp: Date.now()
      });
      
      console.log(`✅ ProductService: Returning ${products.length} filtered products (cached for 5 minutes)`);
      return products;
    } catch (error) {
      console.error('❌ ProductService: Search failed:', error);
      return [];
    }
  }

  async getProductsByCategory(categoryId: string): Promise<Product[]> {
    return this.searchProducts('', categoryId);
  }

  clearCache(): void {
    console.log('🧹 ProductService: Clearing all caches');
    this.productCache.clear();
    this.categoryCache = null;
    this.apiServiceCache = null;
    this.currentUser = null;
  }
}

export const productService = new ProductServiceImpl();